import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ChartProps } from '@app-types/visualization';
import sharedStyles from './charts.module.css';

interface PieChartDataPoint {
  name: string;
  value: number;
  color: string;
}

interface PieTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PieChartDataPoint }>;
  total: number;
}

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}

interface LegendEntry {
  value: string;
  color?: string;
}

const CustomTooltip: React.FC<PieTooltipProps> = ({ active, payload, total }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    
    return (
      <div className={sharedStyles.customTooltip}>
        <p className={sharedStyles.tooltipLabel}>{data.name}</p>
        <p style={{ color: data.color }}>
          {`Value: ${data.value.toLocaleString()}`}
        </p>
        <p className={sharedStyles.textMuted}>
          {`Percentage: ${percentage}%`}
        </p>
      </div>
    );
  }
  return null;
};

const CustomLabel: React.FC<PieLabelProps> = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const legendFormatter = (value: string, entry: LegendEntry) => (
  <span className={sharedStyles.textXs} style={{ color: entry.color }}>
    {value}
  </span>
);

const PieChart: React.FC<ChartProps> = ({ data, config, colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'] }) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  const labelColumn = config?.xAxis?.column || Object.keys(data[0])[0];
  const valueColumn = config?.yAxis?.primary?.columns?.[0] || Object.keys(data[0]).find(key => key !== labelColumn) || Object.keys(data[0])[1];

  const formattedData = data.map((item, index) => ({
    name: String(item[labelColumn]),
    value: Number(item[valueColumn]) || 0,
    color: colors[index % colors.length]
  }));

  const total = formattedData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className={sharedStyles.chartContainer}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <Pie
            data={formattedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={CustomLabel as never}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {formattedData.map((entry) => (
              <Cell key={`cell-${entry.name}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip total={total} />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            formatter={legendFormatter}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChart;
